import { useEffect, useState, useRef, useMemo } from "react";
import { Navigate, useLocation } from "react-router";
import { useAuth } from "../../context/AuthContext";
import axios from "../../lib/axios";
import Loader from "../common/Loader";
import { hasAnyConnectedStore } from "../../utils/storeConnection";

interface SubscriptionGuardProps {
  children: React.ReactNode;
}

interface SubscriptionData {
  current_subscription: {
    plan_key: string;
    status: string;
    trial_ends_at: string | null;
    trial_days_remaining: number;
  } | null;
  merchant?: {
    id: number;
    shop: string;
    user_id: number | null;
    is_connected?: boolean;
  };
  redirect?: boolean;
  redirect_url?: string;
}

// Cache subscription status per shop to avoid repeated API calls
const subscriptionCache = new Map<
  string,
  { status: boolean; timestamp: number }
>();
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes cache

// Global confirmation flag (shared across all instances)
// This prevents infinite loops after approval
let globalSubscriptionConfirmed = false;

// Expose cache and confirmation flag for clearing after approval
if (typeof window !== "undefined") {
  (window as any).subscriptionCache = subscriptionCache;
  // Expose function to clear confirmation flag
  (window as any).__subscriptionGuardModule = {
    clearConfirmation: () => {
      globalSubscriptionConfirmed = false;
      // console.log("Subscription confirmation flag cleared");
    },
    clearCache: () => {
      subscriptionCache.clear();
      // console.log("Subscription cache cleared");
    },
    setConfirmed: () => {
      globalSubscriptionConfirmed = true;
      // console.log("Subscription confirmed globally");
    },
  };
}

// Function to clear cache for a specific shop (call after subscription purchase)
export function clearSubscriptionCache(shop: string, userId?: number) {
  if (userId) {
    subscriptionCache.delete(`${shop}-${userId}`);
  } else {
    // Clear all entries for this shop
    for (const key of subscriptionCache.keys()) {
      if (key.startsWith(`${shop}-`)) {
        subscriptionCache.delete(key);
      }
    }
  }
}

export default function SubscriptionGuard({
  children,
}: SubscriptionGuardProps) {
  const { currentShop, user, loading: authLoading, stores } = useAuth();
  const location = useLocation();
  const [checking, setChecking] = useState(true);
  const [hasSubscription, setHasSubscription] = useState<boolean | null>(null);
  const [isStoreConnected, setIsStoreConnected] = useState<boolean | null>(
    null,
  );
  const checkTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const hasCheckedRef = useRef(false);
  const subscriptionConfirmedRef = useRef(false); // Track if subscription is confirmed active
  const lastCheckTimeRef = useRef<number>(0); // Track when we last checked
  const MIN_CHECK_INTERVAL = 2000; // Minimum 2 seconds between checks

  // Allow access to choose-plan page without subscription check
  const isChoosePlanPage = location.pathname === "/billing/choose-plan";
  const isShopifyAppPage = location.pathname === "/shopify-app";

  // Check if we're in the middle of auto-login from billing return
  const urlParams =
    typeof window !== "undefined"
      ? new URLSearchParams(window.location.search)
      : null;
  const hasAuthTokenInUrl = urlParams?.get("auth_token") !== null;
  const isAutoLoginInProgress = isShopifyAppPage && hasAuthTokenInUrl;

  // Memoize current store connection status to prevent unnecessary re-runs
  const currentStoreConnected = useMemo(() => {
    const currentStore = stores.find((store) => store.shop === currentShop);
    return currentStore?.connected ?? null;
  }, [stores, currentShop]);

  useEffect(() => {
    // CRITICAL: If subscription is already confirmed globally, don't run any checks
    // This prevents infinite loops after approval
    if (globalSubscriptionConfirmed && hasSubscription === true) {
      // console.log(
      //   "Subscription already confirmed globally - skipping all checks",
      // );
      setChecking(false);
      hasCheckedRef.current = true;
      return;
    }

    // Clear any pending timeout
    if (checkTimeoutRef.current) {
      clearTimeout(checkTimeoutRef.current);
    }

    // Skip check if on choose-plan page
    if (isChoosePlanPage) {
      setChecking(false);
      setHasSubscription(true); // Allow access
      hasCheckedRef.current = false; // Reset when on choose-plan page
      subscriptionConfirmedRef.current = false; // Reset confirmation when on choose-plan page
      globalSubscriptionConfirmed = false; // Reset global confirmation when on choose-plan page
      return;
    }

    // If we're in the middle of auto-login, wait for auth to complete
    // But don't wait forever - if we have token and stores, proceed
    if (isAutoLoginInProgress && !user) {
      // Check if we have token and stores loaded - if so, proceed
      const hasToken = localStorage.getItem("token");
      const hasStores = stores.length > 0;

      if (hasToken && hasStores) {
        // Token and stores are available, proceed with check
        // console.log(
        //   "Auto-login: Token and stores available, proceeding with subscription check",
        // );
      } else {
        setChecking(true);
        // Don't check subscription yet - wait for user to be loaded
        return;
      }
    }

    // Skip check if user not authenticated
    if (!user || authLoading) {
      setChecking(false);
      hasCheckedRef.current = false;
      setIsStoreConnected(null);
      return;
    }

    // NEW: If user has just signed up and has no stores yet (no currentShop and empty store list),
    // allow direct dashboard access without any subscription or connection checks.
    if (!currentShop && stores.length === 0) {
      setChecking(false);
      hasCheckedRef.current = true;
      setIsStoreConnected(false);
      setHasSubscription(true); // Treat as allowed; user can connect store and choose plan later
      return;
    }

    // If no current shop selected but stores exist, don't block – skip subscription check
    if (!currentShop) {
      setChecking(false);
      hasCheckedRef.current = false;
      setIsStoreConnected(null);
      return;
    }

    // STEP 1: Check if user has ANY connected store (has access_token)
    // This must be checked BEFORE subscription check
    // If no connected stores, don't check subscription - user needs to connect store first
    const hasAnyConnectedStoreFlag = hasAnyConnectedStore(stores);
    const currentStore = stores.find((store) => store.shop === currentShop);
    const currentStoreIsConnected = currentStore?.connected === true;

    // console.log("Checking user-store connection (preliminary):", {
    //   currentShop,
    //   hasStore: !!currentStore,
    //   currentStoreConnected: currentStoreIsConnected,
    //   hasAnyConnectedStore,
    //   storesCount: stores.length,
    // });

    // If user has no connected stores at all, don't check subscription
    // User needs to connect a store first (install app)
    // Allow dashboard access to show "connect store" message
    if (stores.length > 0 && !hasAnyConnectedStoreFlag) {
      // console.log(
      //   "User has no connected stores (no access_token), skipping subscription check - allowing dashboard access",
      // );
      setIsStoreConnected(false);
      setChecking(false);
      setHasSubscription(true); // Allow dashboard access to show connect store message
      hasCheckedRef.current = true;
      // Don't redirect - allow dashboard to show "connect store" message
      return;
    }

    // If current store exists but is not connected (no access_token)
    // Allow dashboard access to show connect store message
    if (currentStore && !currentStoreIsConnected && !hasAnyConnectedStoreFlag) {
      // console.log(
      //   "Current store not connected (app not installed), allowing dashboard to show connect message",
      // );
      setIsStoreConnected(false);
      setChecking(false);
      setHasSubscription(true); // Allow dashboard access
      hasCheckedRef.current = true;
      return; // Allow dashboard to render connect store message
    }

    // If current store is not connected but user has other connected stores
    if (currentStore && !currentStoreIsConnected && hasAnyConnectedStoreFlag) {
      // console.warn(
      //   "Current store not connected, but user has other connected stores",
      // );
      setIsStoreConnected(false);
      setChecking(false);
      hasCheckedRef.current = false;
      // Still allow access to choose-plan page
      if (!isChoosePlanPage) {
        return <Navigate to="/billing/choose-plan" replace />;
      }
      return;
    }

    // Store is connected or info not available yet - proceed to subscription check
    // API response will have final say on connection status
    if (currentStoreIsConnected === true) {
      setIsStoreConnected(true);
    }

    // CRITICAL: If we've already confirmed subscription is active, stop checking
    // This prevents infinite API calls after subscription purchase
    // Check both local ref and global flag
    if (
      (subscriptionConfirmedRef.current || globalSubscriptionConfirmed) &&
      hasSubscription === true
    ) {
      setChecking(false);
      hasCheckedRef.current = true;
      return; // Exit early - subscription already confirmed
    }

    // Check cache first
    const cacheKey = `${currentShop}-${user.id}`;
    const cached = subscriptionCache.get(cacheKey);
    const now = Date.now();

    // If we have a valid cached result and subscription is active, use it
    if (
      cached &&
      cached.status === true &&
      now - cached.timestamp < CACHE_DURATION
    ) {
      // console.log("Using cached active subscription, skipping API check");
      setHasSubscription(true);
      setChecking(false);
      hasCheckedRef.current = true;
      subscriptionConfirmedRef.current = true; // Mark as confirmed locally
      globalSubscriptionConfirmed = true; // Mark as confirmed globally
      return;
    }

    // If we have a recent negative cache (within 5 seconds), don't check again immediately
    // This prevents rapid-fire checks after approval
    if (cached && cached.status === false && now - cached.timestamp < 5000) {
      // console.log("Recent negative cache found, waiting before re-check");
      setChecking(false);
      hasCheckedRef.current = true;
      return;
    }

    const checkSubscription = async () => {
      try {
        // Update last check time when we actually start checking
        lastCheckTimeRef.current = Date.now();
        setChecking(true);

        // console.log("🔍 Starting subscription check...", {
        //   shop: currentShop,
        //   cacheKey,
        // });

        const response = await axios.get<SubscriptionData>(
          "/billing/choose-plan",
          {
            params: { shop: currentShop },
          },
        );

        // console.log("📦 API Response received:", {
        //   hasRedirect: !!response.data?.redirect,
        //   hasSubscription: !!response.data?.current_subscription,
        //   subscriptionStatus: response.data?.current_subscription?.status,
        //   merchantConnected: response.data?.merchant?.is_connected,
        // });

        // STEP 1: Check if user is connected to the store (from API response) FIRST
        // If store is not connected, skip subscription check and allow dashboard to show connect message
        const merchant = response.data?.merchant;
        const isConnectedFromAPI = merchant?.is_connected;

        if (merchant && isConnectedFromAPI === false) {
          // console.log(
          //   "⚠️ Store not connected to user (from API) - skipping subscription check, allowing dashboard",
          // );
          setIsStoreConnected(false);
          setChecking(false);
          setHasSubscription(true); // Allow dashboard to show connect store message
          hasCheckedRef.current = true;
          return; // Exit - dashboard will show connect store message
        }

        // Store is connected (or connection status not available)
        if (isConnectedFromAPI === true) {
          setIsStoreConnected(true);
        } else if (isConnectedFromAPI === undefined) {
          // Connection status not provided, assume connected if merchant exists
          setIsStoreConnected(!!merchant);
        }

        // CRITICAL: Check subscription status - only if store is connected
        // This must be checked after connection check
        const subscription = response.data?.current_subscription;
        const isActive = subscription && subscription.status === "active";

        // If subscription is active, immediately confirm and stop ALL checking
        if (isActive) {
          // console.log(
          //   "✅✅✅ Subscription is ACTIVE - immediately confirming and stopping ALL checks",
          // );
          setHasSubscription(true);
          setChecking(false);
          hasCheckedRef.current = true;
          subscriptionConfirmedRef.current = true;
          globalSubscriptionConfirmed = true;
          setIsStoreConnected(true); // Store must be connected if subscription is active
          subscriptionCache.set(cacheKey, { status: true, timestamp: now });
          return; // Exit immediately - no further processing needed
        }

        // Check if backend wants to redirect (only for fully active subscriptions)
        // This means user has active subscription and should access dashboard
        if (response.data.redirect && response.data.redirect_url) {
          // User has active subscription, allow access and cache it
          // console.log(
          //   "✅ Subscription active - allowing dashboard access (redirect response)",
          // );
          setHasSubscription(true);
          setChecking(false);
          hasCheckedRef.current = true;
          subscriptionConfirmedRef.current = true; // Mark as confirmed - stop future checks
          globalSubscriptionConfirmed = true; // Mark globally
          setIsStoreConnected(true);
          subscriptionCache.set(cacheKey, { status: true, timestamp: now });
          return;
        }

        // This code should never run if subscription is active (handled above)
        // Subscription is NOT active - handle accordingly
        // console.log("Subscription check result:", {
        //   hasSubscription: !!subscription,
        //   status: subscription?.status,
        //   isActive,
        //   shop: currentShop,
        // });

        const subscriptionStatus = false; // Only false if we reach here

        // console.log("Setting subscription status:", {
        //   subscriptionStatus,
        //   isActive,
        //   subscriptionStatusFromAPI: subscription?.status,
        //   cacheKey,
        // });

        setHasSubscription(subscriptionStatus);
        hasCheckedRef.current = true;

        // Cache the result (only cache active subscriptions for longer)
        if (subscriptionStatus) {
          // console.log("Caching active subscription and confirming");
          subscriptionCache.set(cacheKey, { status: true, timestamp: now });
          subscriptionConfirmedRef.current = true; // Mark as confirmed locally
          globalSubscriptionConfirmed = true; // Mark as confirmed globally - stop future checks
        } else {
          // Cache negative results for shorter time (30 seconds)
          // But only if we're not in post-approval state (might be a timing issue)
          if (!isAutoLoginInProgress) {
            subscriptionCache.set(cacheKey, { status: false, timestamp: now });
            // Clear negative cache after 30 seconds
            setTimeout(() => {
              subscriptionCache.delete(cacheKey);
            }, 30000);
          } else {
            // After approval, don't cache negative results - might be timing issue
            // console.log(
            //   "Post-approval: Not caching negative result, subscription might be activating",
            // );
          }
        }
      } catch (err: any) {
        // console.error("Failed to check subscription:", err);
        // On error, assume no subscription to be safe
        setHasSubscription(false);
        hasCheckedRef.current = true;
      } finally {
        setChecking(false);
      }
    };

    // Only check if we haven't confirmed subscription yet
    // Check both local ref and global flag
    if (
      (subscriptionConfirmedRef.current || globalSubscriptionConfirmed) &&
      hasSubscription === true
    ) {
      setChecking(false);
      hasCheckedRef.current = true;
      return;
    }

    // Clear any existing timeout before setting a new one
    if (checkTimeoutRef.current) {
      clearTimeout(checkTimeoutRef.current);
      checkTimeoutRef.current = null;
    }

    // Prevent checking too frequently (rate limiting)
    const timeSinceLastCheck = now - lastCheckTimeRef.current;
    const shouldCheck =
      (!hasCheckedRef.current ||
        !cached ||
        (cached && now - cached.timestamp >= CACHE_DURATION)) &&
      timeSinceLastCheck >= MIN_CHECK_INTERVAL;

    if (shouldCheck) {
      // Small delay to avoid rapid checks on route changes
      // Increase delay slightly to prevent rapid re-checks
      checkTimeoutRef.current = setTimeout(() => {
        checkSubscription();
      }, 300); // Increased from 100ms to 300ms
    } else {
      // If we checked too recently, use cached value or wait
      if (timeSinceLastCheck < MIN_CHECK_INTERVAL && cached) {
        // console.log("Check too recent, using cached value");
        setHasSubscription(cached.status);
        setChecking(false);
        hasCheckedRef.current = true;
        if (cached.status === true) {
          subscriptionConfirmedRef.current = true;
          globalSubscriptionConfirmed = true;
        }
        return;
      }
      // Use cached value
      // console.log("Using cached subscription status:", cached.status);
      setHasSubscription(cached.status);
      setChecking(false);
      hasCheckedRef.current = true;
      if (cached.status === true) {
        subscriptionConfirmedRef.current = true;
        globalSubscriptionConfirmed = true;
      }
    }

    return () => {
      if (checkTimeoutRef.current) {
        clearTimeout(checkTimeoutRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    currentShop,
    user?.id,
    authLoading,
    isChoosePlanPage,
    isAutoLoginInProgress,
    stores.length,
    currentStoreConnected,
  ]);

  // Show loading while checking connection or subscription
  if (
    checking ||
    authLoading ||
    hasSubscription === null ||
    isStoreConnected === null
  ) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center flex flex-col items-center gap-3">
          <Loader className="w-20 h-20 text-blue-500" />

          {/* <p className="text-gray-600 dark:text-gray-400 text-sm">
            {isStoreConnected === null
              ? "Checking store connection..."
              : "Loading data..."}
          </p> */}
        </div>
      </div>
    );
  }

  // If store is not connected, check if user has any connected stores
  // If no connected stores at all, allow dashboard to show "connect store" message
  // Only redirect if user has other connected stores but current one is not connected
  if (isStoreConnected === false && !isChoosePlanPage) {
    const hasAnyConnected = hasAnyConnectedStore(stores);
    if (!hasAnyConnected) {
      // No stores connected at all - allow dashboard to show connect message
      // console.log(
      //   "No stores connected - allowing dashboard to show connect store message",
      // );
      // Set subscription to true to allow dashboard access
      if (hasSubscription === null || hasSubscription === false) {
        setHasSubscription(true);
      }
      // Don't redirect - let dashboard show the message
    } else {
      // User has other connected stores but current one is not - redirect to choose-plan
      // console.log(
      //   "Current store not connected but user has other connected stores, redirecting to choose-plan",
      // );
      return <Navigate to="/billing/choose-plan" replace />;
    }
  }

  // STRICT: If no subscription found (false) and not on choose-plan page, redirect
  // Also block if hasSubscription is still null (shouldn't happen, but safety check)
  // BUT: Do NOT show Choose Plan until a store is genuinely connected:
  //   - Shopify store must have an access_token
  //   - WordPress/WooCommerce store must have connection_status === 'connected'
  // If no store is connected yet, send the user to onboarding instead.
  if (
    (hasSubscription === false || hasSubscription === null) &&
    !isChoosePlanPage
  ) {
    if (!hasAnyConnectedStore(stores)) {
      // console.log("No connected store, redirecting to /on-boarding");
      return <Navigate to="/on-boarding" replace />;
    }
    // console.log("Redirecting to choose-plan: No active subscription", {
    //   hasSubscription,
    //   currentShop,
    //   pathname: location.pathname,
    // });
    // Redirect to choose-plan without exposing shop in URL
    return <Navigate to="/billing/choose-plan" replace />;
  }

  // Only allow access if has active subscription (true) or on choose-plan page
  if (hasSubscription === true || isChoosePlanPage) {
    return <>{children}</>;
  }

  // Default: block access (shouldn't reach here, but safety)
  // If no store is connected, send to onboarding instead of choose-plan.
  if (!hasAnyConnectedStore(stores)) {
    return <Navigate to="/on-boarding" replace />;
  }
  return <Navigate to="/billing/choose-plan" replace />;
}
